import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lire un flux continu, et rester collé à sa pointe.
 *
 * Rien de commun avec `useLecteur`, malgré le voisinage. Celui-ci enregistre
 * une progression, propose une reprise, marque un film comme vu : autant de
 * notions qui n'ont aucun sens ici. Un direct n'a ni durée, ni progression, ni
 * fin — on ne « reprend » pas un match là où on l'avait laissé.
 *
 * Deux chemins de lecture, dans cet ordre et pas l'autre : hls.js, qui
 * télécharge les segments et les assemble en mémoire, puis le lecteur natif
 * uniquement là où hls.js se déclare inutilisable — c'est-à-dire iOS, le seul
 * qui lise vraiment le HLS tout seul.
 *
 * hls.js n'est chargé qu'au moment où un direct existe. C'est six cents
 * kilo-octets qu'un catalogue n'a aucune raison de télécharger.
 */

/**
 * Au-delà de ce retard sur la pointe du flux, on saute plutôt que de rattraper.
 *
 * L'onglet mis en arrière-plan, une mise en veille, un réseau qui hoquette :
 * le lecteur reprend là où il s'était arrêté et se retrouve à traîner une
 * demi-minute derrière tout le monde. Pour un match, c'est insupportable — la
 * réaction des autres dans le vocal arrive avant l'image.
 *
 * Le seuil reste bien au-dessus du retard normal — trois segments — mais plus
 * aussi haut qu'avant : le rattrapage en douceur (`maxLiveSyncPlaybackRate`)
 * absorbe désormais les petites dérives, et ce saut ne sert plus qu'aux
 * décrochages francs, ceux qu'aucune accélération ne comblerait.
 */
const RETARD_TOLERE = 12;
const VERIFICATION_MS = 5000;

export function useFluxHls({ url, actif }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [erreur, setErreur] = useState(null);
  const [recoit, setRecoit] = useState(false);

  /** Se recoller à la pointe du direct, quel que soit le chemin de lecture. */
  const rejoindreLaPointe = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const hls = hlsRef.current;

    // hls.js sait où est la pointe ; le lecteur natif ne l'expose pas, et il
    // faut la déduire de la fin de ce qui est en mémoire tampon.
    const pointe = hls?.liveSyncPosition
      ?? (video.buffered.length ? video.buffered.end(video.buffered.length - 1) : null);

    if (pointe !== null && pointe - video.currentTime > RETARD_TOLERE) {
      video.currentTime = pointe;
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !url || !actif) return undefined;

    let vivant = true;
    setErreur(null);
    setRecoit(false);

    import('hls.js')
      .then(({ default: Hls }) => {
        if (!vivant) return;

        // hls.js d'abord, le lecteur natif seulement en repli — c'est l'ordre
        // que recommande la bibliothèque, et l'inverse ne marche pas.
        // `canPlayType` répond « maybe » dans des environnements qui ne savent
        // pas lire un HLS pour autant : leur confier le flux donne un lecteur
        // qui échoue sans rien dire. Seul iOS mérite vraiment le chemin natif,
        // et c'est précisément là que hls.js se déclare non supporté.
        if (!Hls.isSupported()) {
          if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = url;
            return;
          }

          setErreur('Ce lecteur ne sait pas lire un direct.');
          return;
        }

        const hls = new Hls({
          // Rester a distance du bord.
          //
          // `lowLatencyMode` colle le lecteur a la pointe du flux, ce qui n'a
          // de sens qu'avec du HLS a faible latence — des segments partiels
          // publies avant d'etre clos. Le notre est ordinaire : coller au bord
          // revient a lire ce qui n'est pas encore arrive, et le moindre depot
          // en retard devient une coupure.
          lowLatencyMode: false,
          // Trois segments de retard. Avec des segments d'une seconde, c'est
          // le principal levier sur la latence : chaque unité coûte une
          // seconde de décalage sur le direct réel.
          liveSyncDurationCount: 3,

          // Rattraper en accélérant, plutôt qu'en sautant.
          //
          // Un lecteur dérive toujours un peu — un hoquet du réseau, un
          // segment tardif — et se retrouve à traîner derrière le direct.
          // Sauter en avant se voit et s'entend ; lire 10 % plus vite pendant
          // quelques secondes ne se remarque pas, et le retard se résorbe de
          // lui-même au lieu de s'accumuler.
          maxLiveSyncPlaybackRate: 1.1,
          // Enjamber un trou plutot que de s'arreter devant : un segment
          // manquant vaut mieux qu'une lecture figee.
          maxBufferHole: 1,
          nudgeMaxRetry: 10,
          // Le manifeste change à chaque segment et ne doit jamais être servi
          // depuis un cache, quel qu'il soit.
          manifestLoadingMaxRetry: 6,
        });

        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_, donnees) => {
          if (!donnees.fatal) return;

          // Une erreur fatale ne veut pas dire une émission finie : l'encodeur
          // redémarre, le réseau saute. On tente de repartir avant d'annoncer
          // quoi que ce soit — c'est ce que fait un téléviseur qui perd le
          // signal une seconde.
          if (donnees.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (donnees.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            // Le détail plutôt qu'une formule : sans console accessible dans
            // l'iframe Discord, c'est la seule chose qui dira pourquoi.
            setErreur(`Diffusion interrompue — ${donnees.details ?? donnees.type}`);
            hls.destroy();
            hlsRef.current = null;
          }
        });

        hls.loadSource(url);
        hls.attachMedia(video);
      })
      .catch(() => {
        if (vivant) setErreur('Le lecteur de direct n’a pas pu être chargé.');
      });

    return () => {
      vivant = false;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      video.removeAttribute('src');
      video.load();
    };
  }, [url, actif]);

  /* ── Rester à la pointe ──────────────────────────────────────────── */

  useEffect(() => {
    if (!actif) return undefined;

    const minuteur = setInterval(rejoindreLaPointe, VERIFICATION_MS);

    // Le cas le plus fréquent, et de loin : l'Activity passée en arrière-plan
    // pendant une partie, puis rouverte. Le lecteur a continué à traîner.
    const surRetour = () => { if (!document.hidden) rejoindreLaPointe(); };
    document.addEventListener('visibilitychange', surRetour);

    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', surRetour);
    };
  }, [actif, rejoindreLaPointe]);

  const proprietes = {
    ref: videoRef,
    playsInline: true,
    controls: true,
    // `auto` et non `none` : il n'y a rien à précharger d'un direct, mais
    // `none` retarde l'attache de la source sur certains lecteurs, et le flux
    // ne démarre alors jamais.
    preload: 'auto',
    onPlaying: () => setRecoit(true),
    onError: (evenement) => {
      const erreurMedia = evenement.currentTarget?.error;
      const causes = {
        1: 'lecture interrompue',
        2: 'réseau',
        3: 'décodage impossible',
        4: 'format refusé par le lecteur',
      };

      setErreur(`Lecture impossible — ${causes[erreurMedia?.code] ?? 'cause inconnue'}`);
    },
  };

  return { proprietes, videoRef, erreur, recoit, rejoindreLaPointe };
}
